import React, { useEffect, useState, useRef, createElement } from 'react';
import { PageHero } from '../components/layout/PageHero';
import { FormStepper } from '../components/supplier/FormStepper';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { UploadIcon, CheckCircleIcon, FileTextIcon, XCircleIcon, AlertCircleIcon, DownloadIcon } from 'lucide-react';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { jsPDF } from 'jspdf';
// Generates a blank, printable Expression of Interest (EOI) form as a PDF
// matching the on-screen form structure, so applicants can fill it offline
// and attach it to their submission.
function downloadBlankEOIForm() {
  const doc = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait'
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 18;
  const pageRight = pageWidth - marginX;
  const GREEN: [number, number, number] = [0, 104, 55];
  const GOLD: [number, number, number] = [252, 209, 22];
  const CHARCOAL: [number, number, number] = [26, 31, 46];
  let y = 0;
  const drawLetterhead = () => {
    doc.setFillColor(...GREEN);
    doc.rect(0, 0, pageWidth, 24, 'F');
    doc.setFillColor(...GOLD);
    doc.rect(0, 24, pageWidth, 2, 'F');
    doc.setFillColor(255, 255, 255);
    doc.circle(marginX + 6, 12, 6, 'F');
    doc.setTextColor(...GREEN);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('AU', marginX + 6, 14, {
      align: 'center'
    });
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('AFRICAN UNION COMMISSION', marginX + 17, 11);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text('Procurement Department', marginX + 17, 16.5);
    doc.text('London Office, United Kingdom', marginX + 17, 21);
    y = 34;
  };
  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - 14) {
      doc.addPage();
      y = 18;
    }
  };
  const sectionHeading = (text: string) => {
    ensureSpace(14);
    doc.setTextColor(...GREEN);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11.5);
    doc.text(text, marginX, y);
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.8);
    doc.line(marginX, y + 1.6, marginX + 24, y + 1.6);
    y += 9;
  };
  const labeledBlank = (label: string) => {
    ensureSpace(9);
    doc.setTextColor(...CHARCOAL);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(label, marginX, y);
    const lw = doc.getTextWidth(label);
    const startX = marginX + lw + 2;
    if (startX < pageRight - 5) {
      doc.setDrawColor(170, 170, 170);
      doc.setLineWidth(0.2);
      doc.line(startX, y + 1, pageRight, y + 1);
    }
    y += 9;
  };
  const checkbox = (label: string) => {
    ensureSpace(7);
    doc.setDrawColor(...CHARCOAL);
    doc.setLineWidth(0.3);
    doc.rect(marginX, y - 3.2, 3.6, 3.6);
    doc.setTextColor(...CHARCOAL);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(label, marginX + 6, y);
    y += 7;
  };
  const paragraph = (text: string, size = 9.5) => {
    doc.setTextColor(70, 70, 70);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, pageRight - marginX);
    ensureSpace(lines.length * 5 + 2);
    doc.text(lines, marginX, y);
    y += lines.length * 5 + 3;
  };
  // ---- Build document ----
  drawLetterhead();
  doc.setTextColor(...CHARCOAL);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('EXPRESSION OF INTEREST (EOI) FORM', marginX, y);
  y += 8;
  paragraph('This Expression of Interest (EOI) form forms part of the supplier engagement process associated with the contract award process and must be completed in full by the interested party. Information provided shall be used solely for official supplier engagement, administration and contract award purposes and shall be treated in confidence.');
  y += 2;
  sectionHeading('SECTION 1: GENERAL INFORMATION');
  labeledBlank('1. Name of Company:');
  labeledBlank('1.1 Parent Company (if applicable):');
  labeledBlank('2. Full Address of the Company:');
  labeledBlank('3. Telephone Number (including country code):');
  labeledBlank('4. WhatsApp Number:');
  labeledBlank('5. Email Address:');
  labeledBlank('6. Name and Title of Contact Person:');
  labeledBlank('7. Type of Organisation (Private Company / State Enterprise / Other):');
  labeledBlank('8. Year Established:');
  labeledBlank('9. Company Registration Number:');
  sectionHeading('SECTION 2: BUSINESS INFORMATION');
  doc.setTextColor(...CHARCOAL);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  ensureSpace(7);
  doc.text('10. Business Category (Tick as applicable)', marginX, y);
  y += 7;
  ['Manufacturer', 'Trading Company', 'Service Provider', 'Contractor', 'Technology Company', 'Supplier', 'Authorized Agent', 'Other'].forEach(c => checkbox(c));
  labeledBlank('11. Area of Specialization:');
  labeledBlank('12. Number of Employees — Total / Full Time / Part Time:');
  ensureSpace(7);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('13. Authorised Representative', marginX, y);
  y += 7;
  labeledBlank('Name:');
  labeledBlank('Title:');
  labeledBlank('Telephone:');
  labeledBlank('Email:');
  sectionHeading('SECTION 3: FINANCIAL & BANKING INFORMATION');
  labeledBlank('Account Name:');
  labeledBlank('Bank Name:');
  labeledBlank('Bank Address:');
  labeledBlank('Account Number:');
  labeledBlank('IBAN:');
  labeledBlank('SWIFT/BIC:');
  labeledBlank('Currency of Account:');
  ensureSpace(7);
  doc.setTextColor(...CHARCOAL);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('14. Audited Financial Statements Available:', marginX, y);
  doc.rect(marginX + 78, y - 3.2, 3.6, 3.6);
  doc.text('Yes', marginX + 84, y);
  doc.rect(marginX + 96, y - 3.2, 3.6, 3.6);
  doc.text('No', marginX + 102, y);
  y += 8;
  doc.text('15. Estimated Annual Turnover', marginX, y);
  y += 7;
  labeledBlank('Current Year:');
  labeledBlank('Previous Year:');
  ensureSpace(7);
  doc.text('16. Registration Documents Submitted', marginX, y);
  y += 7;
  ['Certificate of Incorporation', 'Business Registration Certificate', 'Company Profile', 'Proof of Registered Address', 'Tax Registration Certificate', 'Bank Confirmation Letter'].forEach(d => checkbox(d));
  sectionHeading('DECLARATION');
  paragraph('I hereby certify that the information provided in this Expression of Interest (EOI) Form and all supporting documentation is true, complete and accurate. I further acknowledge and understand that this Expression of Interest does not constitute a binding contract, and that any subsequent supplier approval and formal Contract Agreement shall be subject to the African Union Commission’s evaluation and procurement procedures.');
  y += 2;
  const rows = ['Name', 'Title', 'Date', 'Signature', 'Company Stamp / Seal'];
  const labelColW = 50;
  const rowH = 11;
  rows.forEach(label => {
    ensureSpace(rowH + 2);
    doc.setDrawColor(...CHARCOAL);
    doc.setLineWidth(0.2);
    doc.rect(marginX, y, labelColW, rowH);
    doc.rect(marginX + labelColW, y, pageRight - marginX - labelColW, rowH);
    doc.setTextColor(...CHARCOAL);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(label, marginX + 3, y + rowH / 2 + 1.5);
    y += rowH;
  });
  doc.save('AU-Expression-of-Interest-Form.pdf');
}
declare global {
  interface Window {
    Forminit: any;
  }
}
const FORMINIT_FORM_ID = 'jrj3prirapz';
const MAX_TOTAL_FILE_SIZE_MB = 25;
type DocKey = 'incorporation' | 'businessRegistration' | 'companyProfile' | 'proofOfAddress' | 'taxRegistration' | 'bankConfirmation';
interface DocumentDef {
  key: DocKey;
  label: string;
  required: boolean;
  fieldName: string;
}
const documentDefs: DocumentDef[] = [{
  key: 'incorporation',
  label: 'Certificate of Incorporation',
  required: true,
  fieldName: 'fi-file-incorporation'
}, {
  key: 'businessRegistration',
  label: 'Business Registration Certificate',
  required: true,
  fieldName: 'fi-file-businessRegistration'
}, {
  key: 'companyProfile',
  label: 'Company Profile',
  required: false,
  fieldName: 'fi-file-companyProfile'
}, {
  key: 'proofOfAddress',
  label: 'Proof of Registered Address',
  required: false,
  fieldName: 'fi-file-proofOfAddress'
}, {
  key: 'taxRegistration',
  label: 'Tax Registration Certificate',
  required: false,
  fieldName: 'fi-file-taxRegistration'
}, {
  key: 'bankConfirmation',
  label: 'Bank Confirmation Letter',
  required: false,
  fieldName: 'fi-file-bankConfirmation'
}];
export function SupplierRegistration() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [forminitReady, setForminitReady] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const steps = ['General Information', 'Business Information', 'Financial & Banking', 'Declaration & Submit'];
  const [formData, setFormData] = useState({
    // Step 1
    companyName: '',
    parentCompany: '',
    address: '',
    phone: '',
    whatsappNumber: '',
    email: '',
    contactFullName: '',
    contactTitle: '',
    typeOfOrganisation: '',
    yearEstablished: '',
    registrationNumber: '',
    // Step 2
    categories: [] as string[],
    otherCategory: '',
    areaOfSpecialization: '',
    employeesTotal: '',
    employeesFullTime: '',
    employeesPartTime: '',
    authRepName: '',
    authRepTitle: '',
    authRepPhone: '',
    authRepEmail: '',
    // Step 3
    accountName: '',
    bankName: '',
    bankAddress: '',
    accountNumber: '',
    iban: '',
    swiftBic: '',
    currency: '',
    auditedFinancials: '',
    turnoverCurrent: '',
    turnoverPrevious: '',
    // Step 4
    confirmAccurate: false,
    agreeCodeOfConduct: false,
    understandFalseInfo: false,
    declarationName: '',
    declarationTitle: '',
    declarationDate: '',
    signature: ''
  });
  const [files, setFiles] = useState<Record<DocKey, File | null>>({
    incorporation: null,
    businessRegistration: null,
    companyProfile: null,
    proofOfAddress: null,
    taxRegistration: null,
    bankConfirmation: null
  });
  const fileInputRefs = {
    incorporation: useRef<HTMLInputElement>(null),
    businessRegistration: useRef<HTMLInputElement>(null),
    companyProfile: useRef<HTMLInputElement>(null),
    proofOfAddress: useRef<HTMLInputElement>(null),
    taxRegistration: useRef<HTMLInputElement>(null),
    bankConfirmation: useRef<HTMLInputElement>(null)
  };
  // Load Forminit SDK
  useEffect(() => {
    if (document.getElementById('forminit-sdk')) {
      setForminitReady(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'forminit-sdk';
    script.src = 'https://forminit.com/sdk/v1/forminit.js';
    script.async = true;
    script.onload = () => setForminitReady(true);
    document.body.appendChild(script);
  }, []);
  const categoriesList = ['Manufacturer', 'Trading Company', 'Service Provider', 'Contractor', 'Technology Company', 'Supplier', 'Authorized Agent'];
  // Phone number must be E.164 format AND a real, dialable number per its country.
  const normalizePhone = (raw: string): string => {
    let p = (raw || '').trim().replace(/[\s\-().]/g, '');
    if (p.startsWith('00')) p = '+' + p.slice(2);
    if (!p.startsWith('+') && /^\d{8,}$/.test(p)) p = '+' + p;
    return p;
  };
  const isValidE164 = (phone: string): boolean => {
    if (!/^\+\d{8,15}$/.test(phone)) return false;
    try {
      return isValidPhoneNumber(phone);
    } catch {
      return false;
    }
  };
  const handlePhoneBlur = () => {
    if (!formData.phone) {
      setPhoneError(null);
      return;
    }
    const normalized = normalizePhone(formData.phone);
    if (normalized !== formData.phone) {
      setFormData(prev => ({
        ...prev,
        phone: normalized
      }));
    }
    if (!isValidE164(normalized)) {
      setPhoneError('This does not look like a valid phone number. Please double-check the country code and digits (e.g. +447700900123 for UK, +15550101234 for US, +306900000000 for Greece).');
    } else {
      setPhoneError(null);
    }
  };
  const handleNext = () => {
    if (currentStep === 1) {
      const normalized = normalizePhone(formData.phone);
      if (normalized !== formData.phone) {
        setFormData(prev => ({
          ...prev,
          phone: normalized
        }));
      }
      if (!isValidE164(normalized)) {
        setPhoneError('Please enter a valid, real phone number in international format starting with + and the correct country code (e.g. +447700900123).');
        return;
      }
      setPhoneError(null);
      if (!formData.whatsappNumber.trim()) {
        setWhatsappError('Please provide a WhatsApp number. If you don’t use WhatsApp, enter your telephone number here instead.');
        return;
      }
      setWhatsappError(null);
    }
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setSubmitError(null);
    }
  };
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setSubmitError(null);
    }
  };
  const handleFileChange = (key: DocKey, file: File | null) => {
    setFiles(prev => ({
      ...prev,
      [key]: file
    }));
  };
  const totalFileSizeMB = (): number => {
    return Object.values(files).reduce((sum, f) => sum + (f ? f.size : 0), 0) / (1024 * 1024);
  };
  const handleSubmit = async () => {
    setSubmitError(null);
    // Validation: required declarations + signature
    if (!formData.confirmAccurate || !formData.agreeCodeOfConduct || !formData.understandFalseInfo) {
      setSubmitError('Please tick all three declaration checkboxes to proceed.');
      return;
    }
    if (!formData.declarationName.trim() || !formData.signature.trim()) {
      setSubmitError('Please provide your name and digital signature in the declaration.');
      return;
    }
    // Required docs check
    const missingDocs = documentDefs.filter(d => d.required && !files[d.key]);
    if (missingDocs.length > 0) {
      setSubmitError(`Missing required documents: ${missingDocs.map(d => d.label).join(', ')}. Please return to the Financial & Banking step.`);
      return;
    }
    // Size check
    if (totalFileSizeMB() > MAX_TOTAL_FILE_SIZE_MB) {
      setSubmitError(`Total file size exceeds ${MAX_TOTAL_FILE_SIZE_MB}MB. Please reduce file sizes and try again.`);
      return;
    }
    if (!forminitReady || !window.Forminit) {
      setSubmitError('Submission service is still loading. Please wait a moment and try again.');
      return;
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      // Phone — re-normalize defensively right before send
      const normalizedPhone = normalizePhone(formData.phone);
      if (!isValidE164(normalizedPhone)) {
        setSubmitError('The phone number you entered does not appear to be valid for the country code. Please go back to Step 1 and correct it (e.g. +447700900123).');
        setIsSubmitting(false);
        return;
      }
      // Standard Forminit sender fields
      fd.append('fi-sender-fullName', formData.contactFullName);
      fd.append('fi-sender-email', formData.email);
      fd.append('fi-sender-phone', normalizedPhone);
      // Step 1
      fd.append('fi-text-companyName', formData.companyName);
      fd.append('fi-text-parentCompany', formData.parentCompany);
      fd.append('fi-text-fullAddress', formData.address);
      fd.append('fi-text-whatsappNumber', formData.whatsappNumber);
      fd.append('fi-text-contactTitle', formData.contactTitle);
      fd.append('fi-text-typeOfOrganisation', formData.typeOfOrganisation);
      fd.append('fi-text-yearEstablished', formData.yearEstablished);
      fd.append('fi-text-registrationNumber', formData.registrationNumber);
      // Step 2
      const categoriesPayload = formData.categories.map(c => c === 'Other' && formData.otherCategory.trim() ? `Other: ${formData.otherCategory.trim()}` : c).join(', ');
      fd.append('fi-text-businessCategories', categoriesPayload);
      fd.append('fi-text-areaOfSpecialization', formData.areaOfSpecialization);
      fd.append('fi-text-employeesTotal', formData.employeesTotal);
      fd.append('fi-text-employeesFullTime', formData.employeesFullTime);
      fd.append('fi-text-employeesPartTime', formData.employeesPartTime);
      fd.append('fi-text-authRepName', formData.authRepName);
      fd.append('fi-text-authRepTitle', formData.authRepTitle);
      fd.append('fi-text-authRepPhone', formData.authRepPhone);
      fd.append('fi-text-authRepEmail', formData.authRepEmail);
      // Step 3
      fd.append('fi-text-accountName', formData.accountName);
      fd.append('fi-text-bankName', formData.bankName);
      fd.append('fi-text-bankAddress', formData.bankAddress);
      fd.append('fi-text-accountNumber', formData.accountNumber);
      fd.append('fi-text-iban', formData.iban);
      fd.append('fi-text-swiftBic', formData.swiftBic);
      fd.append('fi-text-currency', formData.currency);
      fd.append('fi-text-auditedFinancials', formData.auditedFinancials);
      fd.append('fi-text-turnoverCurrent', formData.turnoverCurrent);
      fd.append('fi-text-turnoverPrevious', formData.turnoverPrevious);
      // Step 4
      fd.append('fi-text-declarations', [formData.confirmAccurate ? '✓ Confirmed information is accurate' : '', formData.agreeCodeOfConduct ? '✓ Agreed to Supplier Code of Conduct' : '', formData.understandFalseInfo ? '✓ Understood false information consequences' : ''].filter(Boolean).join(' | '));
      fd.append('fi-text-declarationName', formData.declarationName);
      fd.append('fi-text-declarationTitle', formData.declarationTitle);
      fd.append('fi-text-declarationDate', formData.declarationDate);
      fd.append('fi-text-signature', formData.signature);
      // Files
      documentDefs.forEach(d => {
        const f = files[d.key];
        if (f) fd.append(d.fieldName, f);
      });
      const forminit = new window.Forminit();
      const result = await forminit.submit(FORMINIT_FORM_ID, fd);
      if (result && result.error) {
        setSubmitError(result.error.message || 'Submission failed. Please try again.');
        setIsSubmitting(false);
        return;
      }
      setSubmitted(true);
      setIsSubmitting(false);
    } catch (err: any) {
      setSubmitError(err?.message || 'There was a problem submitting your registration. Please try again.');
      setIsSubmitting(false);
    }
  };
  if (submitted) {
    return <div data-id="element-834">
        <PageHero title="Expression of Interest (EOI)" subtitle="Submit an Expression of Interest to participate in AU procurement opportunities" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'Procurement'
      }, {
        label: 'Expression of Interest'
      }]} data-id="element-835" />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-id="element-836">
          <Card data-id="element-837">
            <div className="w-20 h-20 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-838">
              <CheckCircleIcon size={48} className="text-au-green" data-id="element-839" />
            </div>
            <h2 className="text-3xl font-serif text-charcoal mb-3" data-id="element-840">
              Expression of Interest Submitted
            </h2>
            <div className="w-16 h-1 bg-au-gold mx-auto mb-6" data-id="element-841"></div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-842">
              Thank you for submitting your Expression of Interest to the
              African Union Commission. Your submission and supporting documents
              have been received. Our procurement team will review your
              Expression of Interest and contact you within{' '}
              <strong data-id="element-843">5–10 business days</strong>.
            </p>
            <p className="text-sm text-gray-600 mb-8" data-id="element-844">
              For urgent enquiries, contact{' '}
              <a href="mailto:procurement@aucommission.org" className="text-au-green font-medium hover:underline" data-id="element-845">
                procurement@aucommission.org
              </a>
            </p>
            <Button variant="secondary" onClick={() => {
            window.location.href = '/';
          }} data-id="element-846">
              Return to Home
            </Button>
          </Card>
        </div>
      </div>;
  }
  return <div data-id="element-847">
      <PageHero title="Expression of Interest (EOI)" subtitle="Submit an Expression of Interest to participate in AU procurement opportunities" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Procurement'
    }, {
      label: 'Expression of Interest'
    }]} data-id="element-848" />

      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-849">
        <div className="mb-8 bg-au-green-light/50 border border-au-green/20 rounded-lg p-6" data-id="element-850">
          <h2 className="text-xl font-serif text-charcoal mb-3" data-id="element-851">
            Expression of Interest (EOI) Form
          </h2>
          <p className="text-gray-700 text-sm leading-relaxed" data-id="element-852">
            This Expression of Interest (EOI) form forms part of the supplier
            engagement process associated with the contract award process and
            must be completed in full by the interested party. Information
            provided in this form shall be used solely for official supplier
            engagement, administration and contract award purposes and shall be
            treated in confidence.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-853">
            <Button variant="secondary" onClick={downloadBlankEOIForm} className="flex items-center gap-2 text-sm" data-id="element-854">
              <DownloadIcon size={16} data-id="element-855" />
              Download Blank EOI Form (PDF)
            </Button>
            <span className="text-xs text-gray-500" data-id="element-856">
              Prefer to fill it offline? Download the printable form, complete
              it, and attach it under the Registration Documents step.
            </span>
          </div>
        </div>

        <FormStepper currentStep={currentStep} steps={steps} data-id="element-857" />

        <Card data-id="element-858">
          {currentStep === 1 && <div data-id="element-859">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-860">
                SECTION 1: GENERAL INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-861"></div>

              <div className="space-y-5" data-id="element-862">
                <div data-id="element-863">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-864">
                    1. Name of Company <span className="text-au-red" data-id="element-865">*</span>
                  </label>
                  <input type="text" value={formData.companyName} onChange={e => setFormData({
                ...formData,
                companyName: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-866" />
                </div>

                <div data-id="element-867">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-868">
                    1.1 Parent Company (if applicable)
                  </label>
                  <input type="text" value={formData.parentCompany} onChange={e => setFormData({
                ...formData,
                parentCompany: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-869" />
                </div>

                <div data-id="element-870">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-871">
                    2. Full Address of the Company{' '}
                    <span className="text-au-red" data-id="element-872">*</span>
                  </label>
                  <textarea rows={3} value={formData.address} onChange={e => setFormData({
                ...formData,
                address: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-873" />
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-874">
                  <div data-id="element-875">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-876">
                      3. Telephone Number (including country code){' '}
                      <span className="text-au-red" data-id="element-877">*</span>
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} onBlur={handlePhoneBlur} className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green ${phoneError ? 'border-au-red' : 'border-gray-border'}`} placeholder="+447700900123" data-id="element-878" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-879">
                      Use international format starting with + and country code.
                    </p>
                    {phoneError && <p className="mt-1 text-xs text-au-red" data-id="element-880">{phoneError}</p>}
                  </div>
                  <div data-id="element-881">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-882">
                      WhatsApp Number <span className="text-au-red" data-id="element-883">*</span>
                    </label>
                    <input type="tel" value={formData.whatsappNumber} onChange={e => setFormData({
                  ...formData,
                  whatsappNumber: e.target.value
                })} className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green ${whatsappError ? 'border-au-red' : 'border-gray-border'}`} placeholder="+447700900123" data-id="element-884" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-885">
                      Used for quicker contact. If you don’t use WhatsApp, enter
                      your telephone number here instead.
                    </p>
                    {whatsappError && <p className="mt-1 text-xs text-au-red" data-id="element-886">
                        {whatsappError}
                      </p>}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-887">
                  <div data-id="element-888">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-889">
                      4. Email Address <span className="text-au-red" data-id="element-890">*</span>
                    </label>
                    <input type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-891" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-892">
                  <div data-id="element-893">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-894">
                      5. Name of Contact Person{' '}
                      <span className="text-au-red" data-id="element-895">*</span>
                    </label>
                    <input type="text" value={formData.contactFullName} onChange={e => setFormData({
                  ...formData,
                  contactFullName: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-896" />
                  </div>
                  <div data-id="element-897">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-898">
                      Title of Contact Person{' '}
                      <span className="text-au-red" data-id="element-899">*</span>
                    </label>
                    <input type="text" value={formData.contactTitle} onChange={e => setFormData({
                  ...formData,
                  contactTitle: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-900" />
                  </div>
                </div>

                <div data-id="element-901">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-902">
                    6. Type of Organisation{' '}
                    <span className="text-au-red" data-id="element-903">*</span>
                  </label>
                  <select value={formData.typeOfOrganisation} onChange={e => setFormData({
                ...formData,
                typeOfOrganisation: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green bg-white" data-id="element-904">
                    <option value="" data-id="element-905">Select type</option>
                    <option value="Private Company" data-id="element-906">Private Company</option>
                    <option value="State Enterprise" data-id="element-907">State Enterprise</option>
                    <option value="Other" data-id="element-908">Other</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-5" data-id="element-909">
                  <div data-id="element-910">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-911">
                      7. Year Established <span className="text-au-red" data-id="element-912">*</span>
                    </label>
                    <input type="text" value={formData.yearEstablished} onChange={e => setFormData({
                  ...formData,
                  yearEstablished: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-913" />
                  </div>
                  <div data-id="element-914">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-915">
                      8. Company Registration Number{' '}
                      <span className="text-au-red" data-id="element-916">*</span>
                    </label>
                    <input type="text" value={formData.registrationNumber} onChange={e => setFormData({
                  ...formData,
                  registrationNumber: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-917" />
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 2 && <div data-id="element-918">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-919">
                SECTION 2: BUSINESS INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-920"></div>

              <div className="space-y-6" data-id="element-921">
                <div data-id="element-922">
                  <label className="block text-sm font-medium text-charcoal mb-3" data-id="element-923">
                    9. Business Category (Tick as applicable)
                  </label>
                  <div className="grid md:grid-cols-2 gap-3" data-id="element-924">
                    {categoriesList.map(cat => <label key={cat} className="flex items-center gap-2 p-3 border border-gray-border rounded-lg hover:bg-au-green-light cursor-pointer transition-colors" data-id="element-925">
                        <input type="checkbox" checked={formData.categories.includes(cat)} onChange={e => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        categories: [...formData.categories, cat]
                      });
                    } else {
                      setFormData({
                        ...formData,
                        categories: formData.categories.filter(c => c !== cat)
                      });
                    }
                  }} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-926" />
                        <span className="text-sm" data-id="element-927">{cat}</span>
                      </label>)}
                    <label className="flex items-center gap-2 p-3 border border-gray-border rounded-lg hover:bg-au-green-light cursor-pointer transition-colors" data-id="element-928">
                      <input type="checkbox" checked={formData.categories.includes('Other')} onChange={e => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        categories: [...formData.categories, 'Other']
                      });
                    } else {
                      setFormData({
                        ...formData,
                        categories: formData.categories.filter(c => c !== 'Other'),
                        otherCategory: ''
                      });
                    }
                  }} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-929" />
                      <span className="text-sm" data-id="element-930">Other</span>
                    </label>
                  </div>
                  {formData.categories.includes('Other') && <div className="mt-3" data-id="element-931">
                      <input type="text" value={formData.otherCategory} onChange={e => setFormData({
                  ...formData,
                  otherCategory: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Please specify" data-id="element-932" />
                    </div>}
                </div>

                <div data-id="element-933">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-934">
                    10. Area of Specialization{' '}
                    <span className="text-au-red" data-id="element-935">*</span>
                  </label>
                  <textarea rows={3} value={formData.areaOfSpecialization} onChange={e => setFormData({
                ...formData,
                areaOfSpecialization: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-936" />
                </div>

                <div data-id="element-937">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-938">
                    11. Number of Employees
                  </label>
                  <div className="grid grid-cols-3 gap-4" data-id="element-939">
                    <div data-id="element-940">
                      <input type="number" value={formData.employeesTotal} onChange={e => setFormData({
                    ...formData,
                    employeesTotal: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Total" data-id="element-941" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-942">
                      <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-943">
                        Full Time:
                      </span>
                      <input type="number" value={formData.employeesFullTime} onChange={e => setFormData({
                    ...formData,
                    employeesFullTime: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-944" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-945">
                      <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-946">
                        Part Time:
                      </span>
                      <input type="number" value={formData.employeesPartTime} onChange={e => setFormData({
                    ...formData,
                    employeesPartTime: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-947" />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-border pt-6" data-id="element-948">
                  <label className="block text-sm font-medium text-charcoal mb-4" data-id="element-949">
                    12. Authorised Representative
                  </label>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-950">
                    <div data-id="element-951">
                      <input type="text" value={formData.authRepName} onChange={e => setFormData({
                    ...formData,
                    authRepName: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Name" data-id="element-952" />
                    </div>
                    <div data-id="element-953">
                      <input type="text" value={formData.authRepTitle} onChange={e => setFormData({
                    ...formData,
                    authRepTitle: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Title" data-id="element-954" />
                    </div>
                    <div data-id="element-955">
                      <input type="tel" value={formData.authRepPhone} onChange={e => setFormData({
                    ...formData,
                    authRepPhone: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Telephone" data-id="element-956" />
                    </div>
                    <div data-id="element-957">
                      <input type="email" value={formData.authRepEmail} onChange={e => setFormData({
                    ...formData,
                    authRepEmail: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Email" data-id="element-958" />
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 3 && <div data-id="element-959">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-960">
                SECTION 3: FINANCIAL & BANKING INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-961"></div>

              <div className="space-y-5 mb-8" data-id="element-962">
                <div className="grid md:grid-cols-2 gap-4" data-id="element-963">
                  <div data-id="element-964">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-965">
                      Account Name
                    </label>
                    <input type="text" value={formData.accountName} onChange={e => setFormData({
                  ...formData,
                  accountName: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-966" />
                  </div>
                  <div data-id="element-967">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-968">
                      Bank Name
                    </label>
                    <input type="text" value={formData.bankName} onChange={e => setFormData({
                  ...formData,
                  bankName: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-969" />
                  </div>
                </div>

                <div data-id="element-970">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-971">
                    Bank Address
                  </label>
                  <input type="text" value={formData.bankAddress} onChange={e => setFormData({
                ...formData,
                bankAddress: e.target.value
              })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-972" />
                </div>

                <div className="grid md:grid-cols-2 gap-4" data-id="element-973">
                  <div data-id="element-974">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-975">
                      Account Number
                    </label>
                    <input type="text" value={formData.accountNumber} onChange={e => setFormData({
                  ...formData,
                  accountNumber: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-976" />
                  </div>
                  <div data-id="element-977">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-978">
                      IBAN
                    </label>
                    <input type="text" value={formData.iban} onChange={e => setFormData({
                  ...formData,
                  iban: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-979" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4" data-id="element-980">
                  <div data-id="element-981">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-982">
                      SWIFT/BIC
                    </label>
                    <input type="text" value={formData.swiftBic} onChange={e => setFormData({
                  ...formData,
                  swiftBic: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-983" />
                  </div>
                  <div data-id="element-984">
                    <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-985">
                      Currency of Account
                    </label>
                    <input type="text" value={formData.currency} onChange={e => setFormData({
                  ...formData,
                  currency: e.target.value
                })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-986" />
                  </div>
                </div>

                <div className="border-t border-gray-border pt-5" data-id="element-987">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-988">
                    13. Audited Financial Statements Available
                  </label>
                  <div className="flex gap-4" data-id="element-989">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-990">
                      <input type="radio" name="auditedFinancials" value="Yes" checked={formData.auditedFinancials === 'Yes'} onChange={e => setFormData({
                    ...formData,
                    auditedFinancials: e.target.value
                  })} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-991" />
                      <span className="text-sm" data-id="element-992">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-993">
                      <input type="radio" name="auditedFinancials" value="No" checked={formData.auditedFinancials === 'No'} onChange={e => setFormData({
                    ...formData,
                    auditedFinancials: e.target.value
                  })} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-994" />
                      <span className="text-sm" data-id="element-995">No</span>
                    </label>
                  </div>
                </div>

                <div data-id="element-996">
                  <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-997">
                    14. Estimated Annual Turnover
                  </label>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-998">
                    <div className="flex items-center gap-2" data-id="element-999">
                      <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-1000">
                        Current Year:
                      </span>
                      <input type="text" value={formData.turnoverCurrent} onChange={e => setFormData({
                    ...formData,
                    turnoverCurrent: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1001" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1002">
                      <span className="text-sm text-gray-600 whitespace-nowrap" data-id="element-1003">
                        Previous Year:
                      </span>
                      <input type="text" value={formData.turnoverPrevious} onChange={e => setFormData({
                    ...formData,
                    turnoverPrevious: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1004" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-border pt-6" data-id="element-1005">
                <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-1006">
                  15. Registration Documents Submitted
                </h3>
                <p className="text-sm text-gray-600 mb-6" data-id="element-1007">
                  Maximum total file size: {MAX_TOTAL_FILE_SIZE_MB}MB. Accepted
                  formats: PDF, DOC, DOCX, JPG, PNG. Current total:{' '}
                  <span className="font-bold text-au-green" data-id="element-1008">
                    {totalFileSizeMB().toFixed(2)}MB
                  </span>
                </p>
                <div className="space-y-4" data-id="element-1009">
                  {documentDefs.map(doc => {
                const file = files[doc.key];
                return <div key={doc.key} className={`border-2 border-dashed rounded-lg p-6 transition-colors ${file ? 'border-au-green bg-au-green-light' : 'border-gray-border hover:border-au-green'}`} data-id="element-1010">
                        <div className="flex items-center justify-between mb-3" data-id="element-1011">
                          <label className="text-sm font-medium text-charcoal" data-id="element-1012">
                            {doc.label}{' '}
                            {doc.required && <span className="text-au-red" data-id="element-1013">*</span>}
                          </label>
                          {file && <span className="text-xs text-au-green font-medium" data-id="element-1014">
                              {(file.size / (1024 * 1024)).toFixed(2)}MB
                            </span>}
                        </div>

                        <input ref={fileInputRefs[doc.key]} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => {
                    const f = e.target.files?.[0] || null;
                    handleFileChange(doc.key, f);
                  }} className="hidden" data-id="element-1015" />

                        {!file ? <div className="flex items-center gap-4" data-id="element-1016">
                            <Button variant="secondary" onClick={() => fileInputRefs[doc.key].current?.click()} className="flex items-center gap-2" data-id="element-1017">
                              <UploadIcon size={16} data-id="element-1018" />
                              Choose File
                            </Button>
                            <span className="text-sm text-gray-500" data-id="element-1019">
                              No file chosen
                            </span>
                          </div> : <div className="flex items-center justify-between bg-white border border-gray-border rounded-lg p-3" data-id="element-1020">
                            <div className="flex items-center gap-3 min-w-0" data-id="element-1021">
                              <FileTextIcon size={20} className="text-au-green flex-shrink-0" data-id="element-1022" />
                              <span className="text-sm text-charcoal truncate" data-id="element-1023">
                                {file.name}
                              </span>
                            </div>
                            <button type="button" onClick={() => {
                      handleFileChange(doc.key, null);
                      if (fileInputRefs[doc.key].current) {
                        fileInputRefs[doc.key].current!.value = '';
                      }
                    }} className="text-au-red hover:text-red-700 flex-shrink-0 ml-3" aria-label="Remove file" data-id="element-1024">
                              <XCircleIcon size={20} data-id="element-1025" />
                            </button>
                          </div>}
                      </div>;
              })}
                </div>

                {totalFileSizeMB() > MAX_TOTAL_FILE_SIZE_MB && <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-1026">
                    <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-1027" />
                    <span data-id="element-1028">
                      Total file size exceeds {MAX_TOTAL_FILE_SIZE_MB}MB. Please
                      reduce file sizes before submitting.
                    </span>
                  </div>}
              </div>
            </div>}

          {currentStep === 4 && <div data-id="element-1029">
              <h2 className="text-2xl font-bold text-charcoal mb-2" data-id="element-1030">
                DECLARATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-1031"></div>

              <div className="space-y-6" data-id="element-1032">
                <div className="bg-au-green-light/50 border border-au-green/20 rounded-lg p-6" data-id="element-1033">
                  <p className="text-gray-800 text-sm leading-relaxed mb-4" data-id="element-1034">
                    I hereby certify that the information provided in this
                    Expression of Interest (EOI) Form and all supporting
                    documentation is true, complete and accurate.
                  </p>
                  <p className="text-gray-800 text-sm leading-relaxed" data-id="element-1035">
                    I further acknowledge and understand that this Expression of
                    Interest does not constitute a binding contract, and that
                    any subsequent supplier approval and formal Contract
                    Agreement shall be subject to the African Union Commission’s
                    evaluation and procurement procedures.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-1036">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1037">
                    <input type="checkbox" checked={formData.confirmAccurate} onChange={e => setFormData({
                  ...formData,
                  confirmAccurate: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1038" />
                    <span className="text-sm text-gray-700" data-id="element-1039">
                      I confirm that all information provided is accurate and
                      complete
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1040">
                    <input type="checkbox" checked={formData.agreeCodeOfConduct} onChange={e => setFormData({
                  ...formData,
                  agreeCodeOfConduct: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1041" />
                    <span className="text-sm text-gray-700" data-id="element-1042">
                      I agree to the African Union Supplier Code of Conduct
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1043">
                    <input type="checkbox" checked={formData.understandFalseInfo} onChange={e => setFormData({
                  ...formData,
                  understandFalseInfo: e.target.checked
                })} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1044" />
                    <span className="text-sm text-gray-700" data-id="element-1045">
                      I understand that providing false information may result
                      in disqualification
                    </span>
                  </label>
                </div>

                <div className="border-t border-gray-border pt-6" data-id="element-1046">
                  <div className="grid md:grid-cols-2 gap-5 mb-5" data-id="element-1047">
                    <div data-id="element-1048">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1049">
                        Name <span className="text-au-red" data-id="element-1050">*</span>
                      </label>
                      <input type="text" value={formData.declarationName} onChange={e => setFormData({
                    ...formData,
                    declarationName: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1051" />
                    </div>
                    <div data-id="element-1052">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1053">
                        Title
                      </label>
                      <input type="text" value={formData.declarationTitle} onChange={e => setFormData({
                    ...formData,
                    declarationTitle: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1054" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5" data-id="element-1055">
                    <div data-id="element-1056">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1057">
                        Date
                      </label>
                      <input type="date" value={formData.declarationDate} onChange={e => setFormData({
                    ...formData,
                    declarationDate: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" data-id="element-1058" />
                    </div>
                    <div data-id="element-1059">
                      <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-1060">
                        Signature <span className="text-au-red" data-id="element-1061">*</span>
                      </label>
                      <input type="text" value={formData.signature} onChange={e => setFormData({
                    ...formData,
                    signature: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-border rounded-lg focus:outline-none focus:ring-2 focus:ring-au-green" placeholder="Type your full name as signature" data-id="element-1062" />
                    </div>
                  </div>
                </div>

                {submitError && <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-1063">
                    <AlertCircleIcon size={18} className="flex-shrink-0 mt-0.5" data-id="element-1064" />
                    <span data-id="element-1065">{submitError}</span>
                  </div>}
              </div>
            </div>}

          <div className="flex justify-between mt-8 pt-6 border-t border-gray-border" data-id="element-1066">
            <Button variant="secondary" onClick={handlePrevious} disabled={currentStep === 1 || isSubmitting} className="disabled:opacity-50" data-id="element-1067">
              Previous
            </Button>
            {currentStep < steps.length ? <Button variant="primary" onClick={handleNext} data-id="element-1068">
                Next Step
              </Button> : <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2 disabled:opacity-60" data-id="element-1069">
                {isSubmitting ? <>Submitting...</> : <>
                    <CheckCircleIcon size={20} data-id="element-1070" />
                    Submit Registration
                  </>}
              </Button>}
          </div>
        </Card>
      </div>
    </div>;
}